// @ts-nocheck
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import { BASE_URL } from "../Constants/BaseUrl";
import { db } from "../../src/firebase/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import dayjs from "dayjs";
import CryptoJS from "crypto-js";
import { Lock } from "lucide-react";
import {
  fetchCoachPublicProfile,
  fetchUserPublicProfile,
} from "../store/postsSlice";

const ChatListScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);

  const [chats, setChats] = useState([]);
  const [liveProfiles, setLiveProfiles] = useState({}); // ← local cache
  const [loading, setLoading] = useState(true);

  const decryptMessage = (text, encryptionKey) => {
    if (!text || !encryptionKey) return text;
    if (text.startsWith("U2FsdGVkX1")) {
      try {
        const bytes = CryptoJS.AES.decrypt(text, encryptionKey);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted || text;
      } catch (error) {
        return text;
      }
    }
    return text;
  };

  // 1. Listen to chats
  useEffect(() => {
    if (!user?.user_id) {
      setLoading(false);
      return;
    }

    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("users", "array-contains", user.user_id.toString()),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const chatData = snapshot.docs.map((doc) => {
          const data = doc.data();
          const otherUserId = data.users.find(
            (id) => id !== user.user_id.toString(),
          );
          const otherUserDetail = data.usersDetails?.find(
            (u) => u.user_id?.toString() === otherUserId,
          );

          // Prefer live profile
          const liveProfile = otherUserId ? liveProfiles[otherUserId] : null;
          const otherUser = liveProfile || otherUserDetail;

          return {
            id: doc.id,
            lastMessage: {
              ...data.lastMessage,
              text: data.lastMessage?.text
                ? decryptMessage(
                    data.lastMessage.text,
                    data.encryptionKey || "",
                  )
                : data.lastMessage?.text || "No message",
            },
            unseenCount: data.unseenCounts?.[user.user_id?.toString()] || 0,
            otherUserId,
            otherUser,
          };
        });

        const sortedChats = chatData.sort((a, b) => {
          const timeA = a.lastMessage?.timestamp?.seconds || 0;
          const timeB = b.lastMessage?.timestamp?.seconds || 0;
          return timeB - timeA;
        });

        setChats(sortedChats);
        setLoading(false);
      },
      (error) => {
        console.error("Firestore snapshot error:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user?.user_id, liveProfiles]);

  // 2. Fetch live profiles
  useEffect(() => {
    if (!chats.length) return;

    const uniqueIds = [
      ...new Set(chats.map((c) => c.otherUserId).filter(Boolean)),
    ];

    uniqueIds.forEach(async (id) => {
      const key = id.toString();
      if (liveProfiles[key]) return; // already have it

      try {
        // 1. Try athlete first
        let profile = null;
        try {
          profile = await dispatch(fetchUserPublicProfile(key)).unwrap();
        } catch (e) {
          // athlete endpoint failed → ignore
        }

        // 2. Check if we got a real name
        const hasAthleteName =
          profile?.athlete_info?.first_name ||
          profile?.first_name ||
          profile?.username;

        // 3. If no good athlete data → try coach endpoint
        if (!hasAthleteName) {
          try {
            profile = await dispatch(fetchCoachPublicProfile(key)).unwrap();
          } catch (e) {
            console.log("Coach endpoint also failed for", key);
          }
        }

        if (profile) {
          setLiveProfiles((prev) => ({
            ...prev,
            [key]: profile,
          }));
        }
      } catch (e) {
        console.log("Failed to fetch any profile for", key);
      }
    });
  }, [chats, dispatch]);

  // Helpers
  const getDisplayName = (otherUser) => {
    if (!otherUser) return "Unknown";

    const first =
      otherUser?.athlete_info?.first_name ||
      otherUser?.coach_info?.first_name ||
      otherUser?.first_name ||
      otherUser?.username;

    const last =
      otherUser?.athlete_info?.last_name ||
      otherUser?.coach_info?.last_name ||
      otherUser?.last_name;

    if (first && last) return `${first} ${last}`;
    return first || last || "Unknown";
  };

  const getAvatarUri = (otherUser) => {
    const pic = otherUser?.profile_picture;
    if (!pic)
      return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQfj3Bm37Nn_rBQHkIzxnpmGMv3AnLWNYvA1_GwXzebfQ7rxLHl0fRsKo6mIi1SmoOiCL4&usqp=CAU";
    return pic.startsWith("https") ? pic : `${BASE_URL}/${pic}`;
  };

  const formatChatDate = (timestamp) => {
    if (!timestamp?.seconds) return "";
    const messageDate = new Date(timestamp.seconds * 1000);
    const today = new Date();
    const yesterday = dayjs().subtract(1, "day").toDate();

    const isSameDay = (d1, d2) =>
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();

    if (isSameDay(messageDate, today)) return "Today";
    if (isSameDay(messageDate, yesterday)) return "Yesterday";
    return messageDate.toLocaleDateString();
  };

  const handleChatPress = async (chat) => {
    if (!user || !user.user_id) {
      alert("Please log in to access the chat.");
      navigate("/login");
      return;
    }

    try {
      const chatRef = doc(db, "chats", chat.id);
      await updateDoc(chatRef, {
        [`unseenCounts.${user.user_id.toString()}`]: 0,
      });
    } catch (error) {
      console.error("Error resetting unseenCount:", error);
    }

    navigate("/chat-screen", {
      state: {
        chatId: chat.id,
        otherUserId: chat.otherUserId,
        otherUser: chat.otherUser, // still pass it as fallback
        user: {
          user_id: user.user_id,
          username: user.username || "Anonymous",
          profile_picture: user.profile_picture || "",
        },
      },
    });
  };

  // Optional: force refresh button (or call this from a refresh icon)
  const forceRefreshProfiles = () => {
    setLiveProfiles({}); // clear cache → will re-fetch
  };

  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      {/* PRIVATE PROFILE CHECK — EXACT SAME AS REACT NATIVE */}
      {user?.profileVisibility === "private" &&
      user?.role === "student-athlete" ? (
        <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-10 max-w-md shadow-2xl">
            <Lock
              className="w-20 h-20 text-gray-500 mx-auto mb-6"
              strokeWidth={1.5}
            />

            <h2 className="text-2xl font-bold text-white mb-4">
              This Profile is Private
            </h2>

            <p className="text-gray-300 text-base leading-relaxed mb-6">
              Access restricted — this profile is set to private. Public
              visibility is required to view or interact with other users.
            </p>

            <div className="text-sm text-gray-500 bg-gray-800 rounded-lg py-3 px-5 inline-block">
              <span className="font-medium">To make it public:</span>
              <br />
              Profile → Settings → Private/Public Profile
            </div>
          </div>
        </div>
      ) : (
        /* NORMAL CHAT LIST — YOUR ORIGINAL CODE BELOW */
        <>
          {loading ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-gray-400">Loading chats...</p>
            </div>
          ) : chats.length === 0 ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-gray-400">No chats found</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-gray-700">
              {chats.map((item) => {
                const displayName = getDisplayName(item.otherUser);
                const avatarUri = getAvatarUri(item.otherUser);

                return (
                  <div
                    key={item.id}
                    className="flex items-center px-4 py-3 relative cursor-pointer hover:bg-gray-900"
                    onClick={() => handleChatPress(item)}
                  >
                    <img
                      src={avatarUri}
                      alt="avatar"
                      className="w-14 h-14 rounded-full border-2 border-red-600 mr-4 object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-white">{displayName}</p>
                      <p className="text-sm text-gray-400 truncate">
                        {item.lastMessage?.text || "No message"}
                      </p>
                    </div>
                    <div className="text-xs text-gray-400 absolute top-3 right-4">
                      {formatChatDate(item.lastMessage?.timestamp)}
                    </div>
                    {item.unseenCount > 0 && (
                      <div className="absolute bottom-3 right-3 bg-red-500 rounded-full w-5 h-5 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {item.unseenCount}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChatListScreen;
